import { Link } from 'react-router-dom';
import { endpointsForPage } from '../api-endpoints';
import { AnchorHeading } from '../components/AnchorHeading';
import { OnThisPage } from '../components/OnThisPage';
import { useDocsPage } from '../hooks/useDocsPage';

const CURL_SEND = `curl -X POST https://api.revvly.ai/api/v1/product-events \\
  -H "Authorization: Bearer rvly_sk_live_<your-token>" \\
  -H "Content-Type: application/json" \\
  -d '{
    "events": [
      {
        "eventId": "evt_8f2c",
        "event": "report.exported",
        "userId": "u_1042",
        "clientId": "acme",
        "timestamp": "2026-11-01T09:30:00Z",
        "properties": { "format": "pdf", "rows": 240 }
      }
    ]
  }'`;

const RESPONSE = `{
  "results": [
    { "eventId": "evt_8f2c", "status": "ACCEPTED" }
  ]
}`;

const CURL_TOKEN = `curl -X POST https://api.revvly.ai/api/v1/product-events/tokens \\
  -H "Authorization: Bearer rvly_sk_live_<your-token>" \\
  -H "Content-Type: application/json" \\
  -d '{ "userId": "u_1042", "clientId": "acme", "ttlSeconds": 900 }'

# { "token": "eyJ...", "expiresAt": "2026-11-01T10:15:00.000Z" }`;

const BROWSER_SEND = `// In the page, with the token your backend handed it.
await fetch('https://api.revvly.ai/api/v1/product-events/browser', {
  method: 'POST',
  keepalive: true, // lets the request finish while the page closes
  headers: {
    Authorization: \`Bearer \${token}\`,
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    events: [
      {
        event: 'page.viewed',
        sessionId: 's_7f3a',
        properties: { path: '/reports' },
      },
    ],
  }),
});`;

const SDK_SNIPPET = `<script src="https://app.revvly.ai/revvly-events.js"></script>
<script>
  RevvlyEvents.init({
    // Ask YOUR backend for a token: it calls POST /api/v1/product-events/tokens
    // with its API key. Called again before the token expires.
    getToken: () => fetch('/my-backend/revvly-token').then((r) => r.text()),
  });

  // Page views and time on each page are recorded for you. Add your own:
  RevvlyEvents.track('report.exported', { format: 'pdf' });
</script>`;

const CURL_ERASE = `curl -X DELETE https://api.revvly.ai/api/v1/product-events/users/u_1042 \\
  -H "Authorization: Bearer rvly_sk_live_<your-token>"

# { "eventsDeleted": 312 }`;

export function ProductEventsApi() {
  useDocsPage('Product Events API — Revvly Docs');

  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/api">API</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Product events</span>
      </nav>
      <header className="dp-header">
        <span className="rv-eyebrow-mini">API Reference</span>
        <h1>
          Send <em>product events</em> to see how clients use your product.
        </h1>
        <p className="dp-oneliner">
          Report what your users do (a report exported, a feature opened) and Revvly shows it on
          each client&rsquo;s Usage tab, in the cross-client overview and in the support ticket of
          the person who wrote in. Nothing here is billed: for metered, billable usage use the{' '}
          <Link to="/docs/api/events">Event Ingestion API</Link>.
        </p>
      </header>

      <OnThisPage endpoints={endpointsForPage('/docs/api/product-events')} />

      <section className="dp-section">
        <h2>Authentication</h2>
        <p className="dp-section-sub">
          A Bearer API key that holds <span className="api-inline">PRODUCT_EVENT_WRITE</span>{' '}
          (&ldquo;Can send product events&rdquo;). No existing key has it: grant it when you issue a
          key. It is not the same as <span className="api-inline">USAGE_WRITE</span>, and a key for
          one cannot call the other. The workspace is the key&rsquo;s own; nothing in the body names
          a tenant.
        </p>
        <pre className="api-code">{`Authorization: Bearer rvly_sk_live_<your-token>
Content-Type:  application/json`}</pre>
        <div className="api-note">
          <span className="api-note-icon">i</span>
          <span>
            <strong>The key never goes in a page.</strong> It is a secret and must not ship in
            client code. Send from your backend with the key, or, for what only a browser sees
            (pages, time on page), have your backend mint a short-lived token for the signed-in
            person and send with that (see Browser tracking below).
          </span>
        </div>
      </section>

      <section className="dp-section">
        <AnchorHeading id="post-product-events">Send events</AnchorHeading>
        <p className="dp-section-sub">
          <span className="api-inline">POST /api/v1/product-events</span>: 1 to 100 events per
          request, answered <span className="api-inline">202</span> with one result per event. One
          bad event is refused on its own and never sinks the others.
        </p>
        <div className="dp-fields-table api-field-table-mb-sm">
          <div className="dp-field-row">
            <div className="dp-field-k">event</div>
            <div className="dp-field-v">
              Required. The event name, up to 128 characters. Each new name appears in Settings →
              Product events, where an admin says what it means.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">userId / clientId</div>
            <div className="dp-field-v">
              At least one. Your own ids for the person and for their company; the client id is
              matched to the client&rsquo;s external id in Revvly. Up to 255 characters.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">eventId</div>
            <div className="dp-field-v">
              Optional. Your own id; sending the same one again returns{' '}
              <span className="api-inline">DUPLICATE</span> and stores nothing, so retries are safe.
              Without one, Revvly makes an id and a retry cannot be recognised.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">timestamp</div>
            <div className="dp-field-v">
              Optional ISO 8601. Defaults to now. Up to 15 minutes ahead and 30 days back.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">properties</div>
            <div className="dp-field-v">
              Optional flat object, up to 50 keys; values are strings (500 characters), numbers,
              booleans or null. Nested values are refused: send them as separate properties.
            </div>
          </div>
        </div>
        <span className="api-code-label">Request</span>
        <pre className="api-code">{CURL_SEND}</pre>
        <span className="api-code-label">Response</span>
        <pre className="api-code">{RESPONSE}</pre>
        <p className="dp-plain">
          Each result is <span className="api-inline">ACCEPTED</span>,{' '}
          <span className="api-inline">DUPLICATE</span> or{' '}
          <span className="api-inline">REJECTED</span> with a{' '}
          <span className="api-inline">reason</span> that says what to fix.
        </p>
        <div className="api-note">
          <span className="api-note-icon">i</span>
          <span>
            <strong>Secrets are not stored.</strong> A property whose name reads as a credential
            (password, token, secret, api key, card number, cvv, iban) is stored as{' '}
            <span className="api-inline">[redacted]</span>, as is any string value that is an email
            address, a JWT or a valid card number. The key is kept so the property still shows in
            the catalogue; the result lists the names it replaced in{' '}
            <span className="api-inline">redacted</span>. Send ids, not personal data.
          </span>
        </div>
        <p className="dp-plain">Events are kept for 13 months, then removed.</p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="post-product-events-token">Mint a browser token</AnchorHeading>
        <p className="dp-section-sub">
          <span className="api-inline">POST /api/v1/product-events/tokens</span>: your backend asks
          for a token for the person who is signed in. It takes a{' '}
          <span className="api-inline">userId</span> and/or a{' '}
          <span className="api-inline">clientId</span> (at least one) and an optional{' '}
          <span className="api-inline">ttlSeconds</span> (60 to 3600, default 900), and needs the
          same <span className="api-inline">PRODUCT_EVENT_WRITE</span> key. Hand the token to the
          page and mint a new one before it expires. Revoking the key stops every token it minted.
        </p>
        <pre className="api-code">{CURL_TOKEN}</pre>
      </section>

      <section className="dp-section">
        <AnchorHeading id="post-product-events-browser">Send events from the browser</AnchorHeading>
        <p className="dp-section-sub">
          <span className="api-inline">POST /api/v1/product-events/browser</span> with the token as
          a Bearer credential. Same events, validation and masking as the server route, at most 50
          per request, and an optional <span className="api-inline">sessionId</span> that groups a
          visit.
        </p>
        <div className="dp-fields-table api-field-table-mb-sm">
          <div className="dp-field-row">
            <div className="dp-field-k">Who</div>
            <div className="dp-field-v">
              The person and the workspace are the token&rsquo;s. An event that names a different{' '}
              <span className="api-inline">userId</span> or{' '}
              <span className="api-inline">clientId</span> is refused with 403; one that names none
              is stamped with the token&rsquo;s.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Allowed sites</div>
            <div className="dp-field-v">
              The page&rsquo;s origin must be listed in Settings → Product events → Browser tracking
              (scheme and host, for example{' '}
              <span className="api-inline">https://app.example.com</span>; or{' '}
              <span className="api-inline">https://*.example.com</span> for every subdomain, which
              does not include <span className="api-inline">example.com</span> itself or a
              white-label domain: list those on their own lines; up to 100). Any other site gets
              403, so a token handed to the wrong page is useless.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Limits</div>
            <div className="dp-field-v">
              10 requests a second and 300 a minute per person. Over that the answer is 429 with a{' '}
              <span className="api-inline">Retry-After</span> header.
            </div>
          </div>
        </div>
        <pre className="api-code">{BROWSER_SEND}</pre>
      </section>

      <section className="dp-section">
        <h2>Browser SDK</h2>
        <p className="dp-section-sub">
          One script that does the browser half for you: it records a{' '}
          <span className="api-inline">page.viewed</span> on load and on every single-page
          navigation, a <span className="api-inline">page.left</span> with the seconds the page was
          actually visible (<span className="api-inline">activeSeconds</span>), and whatever you{' '}
          <span className="api-inline">track</span>. Events carry a session id that changes after 30
          minutes without activity, so Revvly can show sessions, session length and top pages.
        </p>
        <p className="dp-plain">
          There is no package to install: Revvly serves the script from the dashboard (
          <span className="api-inline">app.revvly.ai</span>), and a page loads the current version
          each time. The step-by-step setup (key, clients, allowed sites, token route) is in{' '}
          <Link to="/docs/guides/product-events-setup">Set up product event tracking</Link>. If your
          pages set a Content Security Policy, allow the script&rsquo;s address under{' '}
          <span className="api-inline">script-src</span> and{' '}
          <span className="api-inline">https://api.revvly.ai</span> under{' '}
          <span className="api-inline">connect-src</span>; the{' '}
          <Link to="/docs/guides/product-events-setup#csp">guide</Link> has the details.
        </p>
        <pre className="api-code">{SDK_SNIPPET}</pre>
        <div className="dp-fields-table api-field-table-mb-sm">
          <div className="dp-field-row">
            <div className="dp-field-k">Pages, not records</div>
            <div className="dp-field-v">
              The query string and hash never leave the browser, and ids (numbers, UUIDs, long hex
              or token-like segments) become <span className="api-inline">:id</span>, so{' '}
              <span className="api-inline">/invoices/8123</span> is reported as{' '}
              <span className="api-inline">/invoices/:id</span>. Other segments go out as they are
              (a username or a short code in a path is not recognised), so if your paths hold
              anything personal name your routes yourself with{' '}
              <span className="api-inline">routePattern</span>, a function from path to route. A
              hash-routed app reports every page as <span className="api-inline">/</span>, because
              the hash is dropped. The page title is not sent unless you pass{' '}
              <span className="api-inline">sendTitle: true</span>; titles often hold names or record
              numbers. A referrer from another site is reduced to the site, one from your own site
              is its cleaned path, and the session id is kept in this browser&apos;s local storage
              under <span className="api-inline">revvly_events_session</span>.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Delivery</div>
            <div className="dp-field-v">
              Events go out in small batches every few seconds and when the page is hidden or
              closed. A network error or a 5xx is retried with backoff, a refused token is replaced
              by asking your backend again, and every event has an id, so a retry never counts
              twice. If Revvly is away for long, the oldest events are dropped rather than the page
              slowed. A request that gets no answer in 15 seconds, or a token call that gets none in
              10, is given up and tried again later, and if your token function fails or returns
              anything but the token string the console says so once.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Privacy</div>
            <div className="dp-field-v">
              <span className="api-inline">RevvlyEvents.optOut()</span> stops sending, clears what
              is queued and remembers the choice in that browser;{' '}
              <span className="api-inline">optIn()</span> reverses it. Pass{' '}
              <span className="api-inline">respectDoNotTrack: true</span> to send nothing when the
              browser says Do Not Track.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Options</div>
            <div className="dp-field-v">
              <span className="api-inline">apiUrl</span> (default{' '}
              <span className="api-inline">https://api.revvly.ai</span>),{' '}
              <span className="api-inline">autoPageViews</span> (default true, turn it off if you
              report pages yourself with <span className="api-inline">pageView(path)</span>),{' '}
              <span className="api-inline">sessionTimeoutMinutes</span>,{' '}
              <span className="api-inline">flushIntervalMs</span>,{' '}
              <span className="api-inline">maxBatch</span> and{' '}
              <span className="api-inline">sendTitle</span> (default false).
            </div>
          </div>
        </div>
        <p className="dp-plain">
          The two events it makes, <span className="api-inline">page.viewed</span> and{' '}
          <span className="api-inline">page.left</span>, appear in Settings → Product events like
          any other; leave them as they are to feed the session views.
        </p>
      </section>

      <section className="dp-section">
        <AnchorHeading id="delete-product-events-user">Erase one user</AnchorHeading>
        <p className="dp-section-sub">
          <span className="api-inline">DELETE /api/v1/product-events/users/:userId</span> removes
          every stored event of that user (a data-subject request), in your workspace only. Same key
          and capability; it answers the number deleted and cannot be undone.
        </p>
        <pre className="api-code">{CURL_ERASE}</pre>
      </section>
    </div>
  );
}
